import { IonContent, IonPage } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { CustomLoading } from '@/components/Loading';
import { SessionHeader } from '@/components/SessionHeader/SessionHeader';
import { toast } from '@/components/Toaster/Toast';

import { DebugOverlay } from '@/utils';
import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';

import { sensorService } from '@/controllers/sensorService';

import { useActiveSessionStore } from '@/hooks/useActiveSessionStore';
import { useDatabase } from '@/hooks/useDatabase';
import { useExitSession } from '@/hooks/useExitSession';

import { useSystemConfigContext } from '@/contexts/SystemConfigContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { SessionScreenContextProvider } from '@/store/SessionScreenStore';

import styles from './Session.module.css';

import { EventListenerToasts } from '../../utils/EventListenerToasts';
import { Screens } from './screens/Screens';

interface LocationState {
  clientId: string;
  date: string;
  therapistId: string;
  clientName: string | null;
  sessionId: string;
  tests: string[];
  session: SessionTypeInDatabase;
}

export const SessionPage = () => {
  const { state } = useLocation<LocationState | undefined>();
  const isHubFlow = useMemo(() => state?.session?.title === 'Gait Observations', [state?.session?.title]);
  const [startDate] = useState<string>(state?.date ?? new Date().toISOString());
  const { exit } = useExitSession(isHubFlow);
  const [loadingSystem, setLoadingSystem] = useState(true);
  const { setConfigId } = useSystemConfigContext();

  const { setActiveSession, setRecordingTypes } = useActiveSessionStore();

  const { userInfo } = useUserInfoContext();

  const { fetchSystem, getFirebaseRecordingTypesByIds } = useDatabase();

  useEffect(() => {
    const init = async () => {
      const organizationId = userInfo.organization_id;
      const sessionTypeId = state?.session.id;

      if (!sessionTypeId || sessionTypeId === undefined) {
        return toast({
          variant: 'error',
          title: 'No session type found',
          description: 'Please contact support',
        });
      }

      await fetchSystem(organizationId, sessionTypeId);

      // Fetch recording types if available
      if (state?.tests && state.tests.length > 0) {
        const types = await getFirebaseRecordingTypesByIds(state.tests);
        if (types === undefined) {
          toast({
            variant: 'error',
            title: 'Failed to load recording types',
            description: 'Please try again or contact support if the problem persists.',
          });
        } else {
          setRecordingTypes(types);
        }
      }

      setLoadingSystem(false);
    };

    init();
    return () => {
      exit();
    };
  }, []);

  // store the state info in the context so that the screens can access it when needed
  useEffect(() => {
    if (state?.sessionId && state?.tests) {
      setActiveSession(state?.sessionId, state?.tests);
    }
  }, [state?.sessionId, state?.tests]);

  return (
    <SessionScreenContextProvider>
      <EventListenerToasts />
      <IonPage>
        <SessionHeader
          clientId={state?.clientId ?? ''}
          isHubFlow={isHubFlow}
          startDate={startDate}
          title={state?.clientName ?? 'Unknown'}
        />

        <div className={styles.background}>
          <IonContent className={styles.content} fullscreen id='main'>
            {loadingSystem ? (
              <CustomLoading className={styles.loading} message='Initializing system...' />
            ) : (
              <Screens isHubFlow={isHubFlow} />
            )}
          </IonContent>
        </div>

        <DebugOverlay initiallyVisible={false} />
      </IonPage>
    </SessionScreenContextProvider>
  );
};
